import { useEffect, useMemo, useRef, useState } from 'react';

export interface NearbySpot {
  id: string;
  name: string;
  lat: number;
  lng: number;
  description?: string;
  imageUrl?: string;
  address?: string;
}

export interface UseNearbySpotsParams {
  supabaseClient: any;
  cardId?: string | null;
  coords?: { lat: number; lng: number } | null;
  limit?: number;
}

export interface UseNearbySpotsResult {
  spots: NearbySpot[];
  loading: boolean;
  error: string | null;
}

function extractAddressString(value: any): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean)
      .join(' ')
      .trim();
    return joined.length ? joined : undefined;
  }
  if (typeof value === 'object') {
    const keys = ['formatted', 'fullAddress', 'address', 'name', 'street', 'city', 'district', 'province', 'state'];
    const parts = keys
      .map((key) => {
        const payload = (value as Record<string, unknown>)[key];
        return typeof payload === 'string' ? payload.trim() : undefined;
      })
      .filter((segment): segment is string => !!segment && segment.length > 0);
    if (parts.length > 0) return parts.join(' ');
  }
  return undefined;
}

export function useNearbySpots({ supabaseClient, cardId, coords, limit = 20 }: UseNearbySpotsParams): UseNearbySpotsResult {
  const [spots, setSpots] = useState<NearbySpot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    return () => {
      cancelRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!coords) {
      setSpots([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchNearby = async () => {
      setLoading(true);
      setError(null);
      try {
        const delta = 0.045; // ~5km bbox
        let query: any = supabaseClient
          .from('cards_cards')
          .select('id,title,address,description,card_type,latitude,longitude,cover_image,base_payload')
          .eq('status', 'active')
          .eq('visibility', 'public')
          .gte('latitude', coords.lat - delta)
          .lte('latitude', coords.lat + delta)
          .gte('longitude', coords.lng - delta)
          .lte('longitude', coords.lng + delta)
          .limit(limit);

        if (cardId) {
          query = query.neq('id', cardId);
        }

        const { data, error } = await query;
        if (error) throw error;

        const next: NearbySpot[] = (data || [])
          .map((row: any) => {
            const lat = typeof row.latitude === 'number' ? row.latitude : Number(row.latitude);
            const lng = typeof row.longitude === 'number' ? row.longitude : Number(row.longitude);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
            const name = row.title || '未命名地点';
            const description = typeof row.description === 'string' ? row.description : '';
            let base: any = row.base_payload;
            if (base && typeof base === 'string') {
              try { base = JSON.parse(base); } catch { base = {}; }
            }
            if (!base || typeof base !== 'object') base = {};
            const imageCandidate = row.cover_image || base.cover_image || base.image || base.image_url || base.imageUrl;
            const imageUrl = typeof imageCandidate === 'string' ? imageCandidate : undefined;
            const addressCandidate =
              extractAddressString(row.address) ||
              extractAddressString(base.address) ||
              extractAddressString(base.location_name) ||
              extractAddressString(base.placeName) ||
              extractAddressString(base.location) ||
              extractAddressString(description);
            const addressText = addressCandidate || undefined;
            return { id: row.id, name, description, lat, lng, imageUrl, address: addressText } as NearbySpot;
          })
          .filter(Boolean) as NearbySpot[];

        if (!cancelled && !cancelRef.current) setSpots(next);
      } catch (err: any) {
        console.error('[useNearbySpots] fetch error', err);
        if (!cancelled && !cancelRef.current) setError(err?.message || '加载附近景点失败');
      } finally {
        if (!cancelled && !cancelRef.current) setLoading(false);
      }
    };

    fetchNearby();
    return () => { cancelled = true; };
  }, [supabaseClient, cardId, coords?.lat, coords?.lng, limit]);

  return { spots, loading, error };
}

export default useNearbySpots;
